import React, { useState } from "react";
import { useAppContext } from "../components/AppContext";
import {
  darkModeBorder,
  darkModeText,
  darkModeWidgetBackground,
  darkModeWidgetBorderColor,
  lightModeBorder,
  lightModeText,
  lightModeWidgetBackground,
  lightModeWidgetBorderColor,
} from "../util/style";
import { TradeTemplate } from "../types";

interface UploadProps {
  handleReseed: () => void;
  handleAddTrade: (formField: TradeTemplate) => void;
  handleDownloadTrades: () => void;
  handleUploadJSON: () => void;
  handleReloadSplitSeed: () => void;
  seedAccountInfo: () => void;
}

interface DataOption {
  title: string;
}

const Upload: React.FC<UploadProps> = ({
  handleReseed,
  handleAddTrade,
  handleDownloadTrades,
  handleUploadJSON,
  handleReloadSplitSeed,
  seedAccountInfo,
}) => {
  const {
    darkMode,
    bulkText,
    setBulkText,
    getFont,
    replaceMode,
    setReplaceMode,
    fieldObj,
    setFieldObj,
    privateMode,
    adminMode,
  } = useAppContext() || {};

  const handleInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldObj?.((prev: TradeTemplate) => ({ ...prev, [`${e.target.name}`]: e.target.value }));
  };

  const [uploadPage, setUploadPage] = useState<string>("bulkTrade");

  const dataOptions: Record<string, DataOption> = {
    singleTrade: { title: "Single Trade" },
    bulkTrade: { title: "Bulk Trade" },
    split: { title: "Add Split" },
    downloadSplit: { title: "Download Split" },
    download: { title: "Download Trade" },
    reload: { title: "Reload Trade Seed" },
    reloadSplit: { title: "Reload Split Seed" },
    reloadSeedAccountInfo: { title: "Reload Seed Account Info" },
  };

  return (
    <div
      style={{
        height: "calc(100% - 0px)",
        padding: "10px 10px 10px 10px",
        overflow: "hidden",
        fontSize: getFont?.(12),
      }}
    >
      <div
        style={{
          background: darkMode
            ? darkModeWidgetBackground
            : lightModeWidgetBackground,
          height: "100%",
          width: "100%",
          borderRadius: "2px",
          overflow: "auto",
          border: `1px solid ${
            darkMode ? darkModeWidgetBorderColor : lightModeWidgetBorderColor
          }`,
        }}
        className="hoverShadow"
      >
        <div
          style={{
            display: "flex",
            overflowY: "auto",
            width: "calc(100% - 20px)",
            height: "calc(100% - 20px)",
            margin: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginRight: "10px",
              borderRight: `1px solid ${
                darkMode ? darkModeBorder : lightModeBorder
              }`,
              height: "100%",
              paddingRight: "10px",
            }}
          >
            <div
              style={{
                padding: "5px 5px 5px 5px",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              {Object.keys(dataOptions)?.map((eachKey, idx) => (
                <div
                  key={idx}
                  style={{
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: getFont?.(14),
                    color: uploadPage === eachKey ? "" : "grey",
                  }}
                  onClick={() => {
                    setUploadPage(eachKey);
                  }}
                >
                  {dataOptions[eachKey]?.title}
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: "5px 0px 0 0px" }}>
            {uploadPage === "download" && (
              <>
                <div>Download all trades in JSON</div>
                <div
                  onClick={() => {
                    if (privateMode || adminMode) {
                      handleDownloadTrades();
                    }
                  }}
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    width: "fit-content",
                    opacity: !adminMode && !privateMode ? "0.5" : "",
                    border: "1px solid rgb(100,100,100)",
                  }}
                >
                  Download
                  {!adminMode && !privateMode ? "(Locked)" : ""}
                </div>
              </>
            )}
            {uploadPage === "reload" && (
              <>
                <div>Delete and reload from seed data</div>
                <div
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    width: "fit-content",
                    opacity: !adminMode && !privateMode ? "0.5" : "",
                    border: "1px solid rgb(100,100,100)",
                  }}
                  onClick={() => {
                    if (!adminMode) return;
                    handleReseed();
                  }}
                >
                  Reload Seed {!adminMode ? "(Locked)" : ""}
                </div>
              </>
            )}
            {uploadPage === "reloadSeedAccountInfo" && (
              <>
                <div>Delete and reload Account Info from seed</div>
                <div
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    width: "fit-content",
                    opacity: !adminMode && !privateMode ? "0.5" : "",
                    border: "1px solid rgb(100,100,100)",
                  }}
                  onClick={() => {
                    if (!adminMode) return;
                    seedAccountInfo();
                  }}
                >
                  Reload Seed Account Info {!adminMode ? "(Locked)" : ""}
                </div>
              </>
            )}
            {uploadPage === "reloadSplit" && (
              <>
                <div>Delete and reload from seed data</div>
                <div
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    width: "fit-content",
                    opacity: !adminMode && !privateMode ? "0.5" : "",
                    border: "1px solid rgb(100,100,100)",
                  }}
                  onClick={() => {
                    if (!adminMode) return;
                    handleReloadSplitSeed();
                  }}
                >
                  Reload Split Seed {!adminMode ? "(Locked)" : ""}
                </div>
              </>
            )}
            {uploadPage === "singleTrade" && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>Enter Single Trade</div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "fit-content",
                    gap: "3px",
                  }}
                >
                  <div style={{ display: "flex" }}>
                    <div>
                      <div>Date (MM/DD/YYYY)</div>
                      <input
                        type="text"
                        name="date"
                        placeholder="6/12/2025"
                        value={fieldObj?.date || ""}
                        autoComplete="off"
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                      />
                    </div>
                    <div>
                      <div>Account</div>
                      <input
                        type="text"
                        name="account"
                        placeholder="i.e 1324ACB"
                        value={fieldObj?.account || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                    <div>
                      <div>Currency</div>
                      <input
                        autoComplete="off"
                        type="text"
                        name="currency"
                        value={fieldObj?.currency || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex" }}>
                    <div>
                      <div>Ticker</div>
                      <input
                        autoComplete="off"
                        type="text"
                        name="ticker"
                        placeholder="i.e. TSLA"
                        value={fieldObj?.ticker || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                    <div>
                      <div>Price</div>
                      <input
                        autoComplete="off"
                        type="number"
                        name="price"
                        placeholder="i.e 100"
                        value={fieldObj?.price || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                    <div>
                      <div>Shares</div>
                      <input
                        autoComplete="off"
                        type="number"
                        name="share"
                        placeholder="i.e 200"
                        value={fieldObj?.share || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                    <div>
                      <div>Commission</div>
                      <input
                        type="text"
                        name="commission"
                        placeholder="i.e 10"
                        autoComplete="off"
                        value={fieldObj?.commission || ""}
                        style={{
                          outline: "none",
                          border: "1px solid grey",
                          color: darkMode ? darkModeText : lightModeText,
                          background: darkMode
                            ? darkModeWidgetBackground
                            : lightModeWidgetBackground,
                          fontSize: getFont?.(12),
                        }}
                        onChange={(e) => {
                          handleInputValue(e);
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => {
                    if (!adminMode) return;
                    if (fieldObj) {
                      handleAddTrade(fieldObj);
                    }
                  }}
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    opacity: adminMode ? "" : "0.5",
                    width: "fit-content",
                    border: "1px solid rgb(100,100,100)",
                  }}
                >
                  Add {!adminMode ? "(Locked)" : ""}
                </div>
              </div>
            )}
            {/* upload json */}
            {uploadPage === "bulkTrade" && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div>Enter Bulk</div>
                <div
                  style={{
                    opacity: replaceMode ? "1" : "0.5",
                    cursor: "pointer",
                    padding: "1px 3px",
                    border: "1px solid rgb(100,100,100)",
                    width: "fit-content",
                  }}
                  onClick={() => {
                    setReplaceMode?.(!replaceMode);
                  }}
                >
                  Replace&nbsp;Mode
                </div>
                <textarea
                  rows={20}
                  cols={80}
                  placeholder={`Sample: \n[{"date": "1/1/2020", \n"ticker": "TSLA", \n"price": 200, \n"share": 100, \n"account": \n"1324ACB", \n"currency": "USD", \n"commission": 9.99}, \n{"date": "6/1/2020", \n"ticker": "TSLA", \n"price": 300, \n"share": -50, \n"account": "1324ACB", \n"currency": "USD", \n"commission": 9.99}]`}
                  value={bulkText}
                  onChange={(e) => setBulkText?.(e.target.value)}
                  style={{
                    fontFamily: "monospace",
                    width: "300px",
                    padding: "0.5rem",
                    outline: "none",
                    border: "1px solid grey",
                    color: darkMode ? darkModeText : lightModeText,
                    background: darkMode
                      ? darkModeWidgetBackground
                      : lightModeWidgetBackground,
                    fontSize: getFont?.(12),
                  }}
                />
                <div
                  style={{
                    padding: "2px 8px",
                    cursor: !adminMode && !privateMode ? "" : "pointer",
                    width: "fit-content",
                    opacity: !adminMode && !privateMode ? "0.5" : "",
                    border: "1px solid rgb(100,100,100)",
                  }}
                  onClick={() => {
                    if (!adminMode) return;
                    handleUploadJSON();
                  }}
                >
                  Upload JSON {!adminMode ? "(Locked)" : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;
